import { useState, useEffect, useCallback } from 'react';

/**
 * Belirli bir alan için daha önce kullanılan değerleri izleyen ve öneri olarak sunan hook
 * Tamamen localStorage tabanlı çalışır (client-side only)
 * 
 * @param {string} fieldName - İzlenecek alanın adı (örn: 'project_name')
 * @param {string} initialValue - Alanın başlangıç değeri 
 * @param {string} dealerId - Bayi ID'si (kullanıcıya özel öneri kaydetmek için)
 * @returns {Object} - Değer, değiştirme fonksiyonu, öneriler ve seçim fonksiyonu
 */
const useFieldSuggestions = (fieldName, initialValue = '', dealerId) => {
  const [value, setValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Önerileri localStorage'dan yükle
  const loadSuggestions = useCallback(() => {
    setLoading(true);
    
    try {
      const storedSuggestions = localStorage.getItem(`${dealerId}_${fieldName}_suggestions`);
      
      if (storedSuggestions) {
        try {
          const parsedSuggestions = JSON.parse(storedSuggestions);
          if (Array.isArray(parsedSuggestions)) {
            setSuggestions(parsedSuggestions);
          } else {
            setSuggestions([]);
            localStorage.setItem(`${dealerId}_${fieldName}_suggestions`, JSON.stringify([]));
          }
        } catch (parseError) {
          setSuggestions([]);
          localStorage.setItem(`${dealerId}_${fieldName}_suggestions`, JSON.stringify([]));
        }
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      setSuggestions([]);
      try {
        localStorage.setItem(`${dealerId}_${fieldName}_suggestions`, JSON.stringify([]));
      } catch (storageError) {
        // LocalStorage erişim hatası — sessizce devam et
      }
    } finally {
      setLoading(false);
    }
  }, [fieldName, dealerId]);
  
  // Sayfa yüklendiğinde önerileri getir
  useEffect(() => {
    if (dealerId) {
      loadSuggestions();
    }
  }, [loadSuggestions, dealerId]);
  
  // Değeri güncelle ve önerileri filtrele
  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    
    if (newValue.trim().length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };
  
  // Değeri localStorage'a kaydet
  const saveSuggestion = (value) => {
    if (!dealerId || !value || value.trim() === '') return;
    
    try {
      const key = `${dealerId}_${fieldName}_suggestions`;
      let updatedSuggestions = [...suggestions];
      
      if (!updatedSuggestions.includes(value)) {
        updatedSuggestions = [value, ...updatedSuggestions].slice(0, 10); // En fazla 10 öneri sakla
        setSuggestions(updatedSuggestions);
        
        try {
          localStorage.setItem(key, JSON.stringify(updatedSuggestions));
        } catch (storageError) {
          // Sessizce devam et
        }
      }
    } catch (error) {
      // Kritik olmayan işlem, sessizce devam et
    }
  };
  
  // Bir öneriyi seç
  const selectSuggestion = (selectedValue) => {
    setValue(selectedValue);
    setShowSuggestions(false);
  };
  
  // Filtre uygulanmış öneriler
  const filteredSuggestions = value
    ? suggestions.filter(suggestion => 
        suggestion.toLowerCase().includes(value.toLowerCase()))
    : [];
  
  return {
    value,
    onChange: handleChange,
    suggestions: filteredSuggestions,
    showSuggestions,
    setShowSuggestions,
    selectSuggestion,
    saveSuggestion,
    loading
  };
};

export default useFieldSuggestions;